import { useDispatch, useSelector } from 'react-redux'
import store, { AppDispatch, RootState } from '../../store'
import { useCallback, useEffect } from 'react'
import {
  ActionManager,
  BoundingInfo,
  Color4,
  CreateBoxVertexData,
  ExecuteCodeAction,
  Matrix,
  Mesh,
  Nullable,
  PointerDragBehavior,
  Vector3,
  VertexBuffer,
} from '@babylonjs/core'
import {
  computeBoundingBox,
  createBimObjectTemplateMesh,
  createWallGaps,
  cutOpeningHolesInWallMesh,
  isBimObjectMesh,
  isDoorMesh,
  isSketchMesh,
  isWallMesh,
  isWindowMesh,
  removeBimObjectTemplateMesh,
  syncronizeBimObjectMeshWithTemplate,
} from '../utils'
import { equalsWithEpsilon } from '../../utils/Common'
import { editWindow } from '../../actions/construction'
import { updateHistory } from '../../actions/history'
import { ActionType } from '../../reducers/history'
import { SelectionManager } from '../selection/selectionManager'
import { SeparateElementUI } from './useSeparateWallTool'
import { SceneManager } from '../sceneManager'
import {
  isAntiParallel,
  isOrthoLinear,
  rayBoundingBoxIntersection,
} from '../../utils/MathUtil'
import { createSelector } from '@reduxjs/toolkit'

const bimObjectsSelector = createSelector(
  [(state: RootState) => state.bim],
  (bim) => bim.objects,
)

export enum WindowProperty {
  Width = 'Width',
  Height = 'Height',
  CenterLocationOnWall = 'CenterLocationOnWall',
  SillHeight = 'SillHeight',
}

enum WindowAnchor {
  Start = 'Start',
  Center = 'Center',
  End = 'End',
}

type ChangeWindowPositionOptions = {
  centerLocationOnWall?: number
  sillHeight?: number
}

type SaveResetOptions = WindowProperty | WindowProperty[]

const calculateWindowProps = (windowMesh: Mesh, wallMesh: Mesh) => {
  let windowBoundingBox
  let shiftX
  let shiftY
  if (windowMesh.geometry) {
    windowBoundingBox = windowMesh.getBoundingInfo().boundingBox
    shiftX = windowMesh.position.x
    shiftY = windowMesh.position.y
  } else {
    const bbox = computeBoundingBox(windowMesh.getChildMeshes(), true)
    windowBoundingBox = new BoundingInfo(
      bbox.min,
      bbox.max,
      windowMesh.getWorldMatrix(),
    ).boundingBox
    const wallLocal = Vector3.TransformCoordinates(
      windowBoundingBox.centerWorld.clone(),
      wallMesh.getWorldMatrix().clone().invert(),
    )
    shiftX = wallLocal.x
    shiftY = wallLocal.y
  }
  const windowHalfWidth = windowBoundingBox.extendSize.x * windowMesh.scaling.x
  const windowHalfHeight = windowBoundingBox.extendSize.y * windowMesh.scaling.y
  const wallBoundingBox = wallMesh.getBoundingInfo().boundingBox
  const { x: wallHalfLength, y: wallHalfHeight } = wallBoundingBox.extendSize
  return {
    width: windowHalfWidth * 2,
    height: windowHalfHeight * 2,
    centerLocationOnWall: wallHalfLength + shiftX,
    sillHeight: wallHalfHeight - windowHalfHeight + shiftY,
  }
}

interface EditWindowUtils {
  readonly changeWindowWidth: (value: number, anchor?: WindowAnchor) => void
  readonly changeWindowHeight: (value: number, anchor?: WindowAnchor) => void
  readonly changeWindowPosition: (payload: ChangeWindowPositionOptions) => void
  readonly saveChanges: (options?: SaveResetOptions) => void
  readonly resetChanges: (options?: SaveResetOptions) => void
}

type WindowProperties = {
  [WindowProperty.CenterLocationOnWall]: number
  [WindowProperty.SillHeight]: number
  [WindowProperty.Width]: {
    value: number
    anchor: WindowAnchor
  }
  [WindowProperty.Height]: {
    value: number
    anchor: WindowAnchor
  }
}

const windowPropsMap = new Map<
  string,
  { count: number; props: WindowProperties }
>()

export const useEditWindowTool = (
  windowMesh: Mesh,
  addAction: boolean = true,
): EditWindowUtils => {
  const dispatch = useDispatch<AppDispatch>()
  const bimObjects = useSelector(bimObjectsSelector)
  const bimObject = bimObjects.find(
    (bimObject) => bimObject.id === windowMesh.metadata.bimObjectId,
  )

  const changeWindowWidth = useCallback(
    (value: number, anchor: WindowAnchor = WindowAnchor.Center) => {
      const wallMesh =
        windowMesh.parent instanceof Mesh && isWallMesh(windowMesh.parent)
          ? (windowMesh.parent as Mesh)
          : undefined
      if (!isWindowMesh(windowMesh) || !wallMesh) {
        console.warn(
          `useEditWindowTool supports only meshes of 'Window' type with parent mesh of 'Wall' type`,
          {
            mesh: windowMesh,
            parent: windowMesh.parent,
          },
        )

        return
      }

      const windowProps = windowPropsMap.get(windowMesh.id)?.props
      if (!windowProps) {
        return
      }

      const newWidth = Math.max(value, 1e-3)

      let windowMeshWithGeometry
      if (!isBimObjectMesh(windowMesh)) {
        windowMeshWithGeometry = windowMesh
      } else {
        if (windowMesh.metadata.templateMeshId) {
          windowMeshWithGeometry = windowMesh
            .getScene()
            .getMeshById(windowMesh.metadata.templateMeshId) as Mesh
        }
      }
      if (!windowMeshWithGeometry) {
        windowMeshWithGeometry = createBimObjectTemplateMesh(windowMesh)
      }

      const windowBoundingBox =
        windowMeshWithGeometry.getBoundingInfo().boundingBox
      const {
        x: windowHalfWidth,
        y: windowHalfHeight,
        z: windowHalfThickness,
      } = windowBoundingBox.extendSize

      // No need to resize window if current width is equal to passed value
      if (equalsWithEpsilon(newWidth, windowHalfWidth * 2)) {
        return
      }

      const sketchMeshes = windowMeshWithGeometry
        .getChildMeshes(true, (node): node is Mesh => node instanceof Mesh)
        .reduce<Mesh[]>((acc, current) => {
          if (isSketchMesh(current)) {
            acc.push(current)
          }

          return acc
        }, [])

      const vertexData = CreateBoxVertexData({
        width: newWidth,
        height: windowHalfHeight * 2,
        depth: windowHalfThickness * 2,
        faceColors: [
          Color4.FromInts(255, 255, 255, 255),
          Color4.FromInts(255, 255, 255, 255),
          Color4.FromInts(255, 255, 255, 255),
          Color4.FromInts(255, 255, 255, 255),
          Color4.FromInts(0, 0, 0, 255),
          Color4.FromInts(255, 255, 255, 255),
        ],
      })

      vertexData.applyToMesh(windowMeshWithGeometry)

      if (anchor === WindowAnchor.Start || anchor === WindowAnchor.End) {
        const diff = newWidth - windowHalfWidth * 2
        windowMeshWithGeometry.position.x +=
          anchor === WindowAnchor.Start ? diff / 2 : -diff / 2
      }

      // Trigger Measurements UI position change
      windowMeshWithGeometry.computeWorldMatrix(true)

      const selectionManager = SelectionManager.getInstance()
      if (selectionManager.isSelected(windowMeshWithGeometry.id)) {
        // Update selection edges
        windowMesh.enableEdgesRendering(0.9999)
      }

      // Update Sketch mesh of window
      if (vertexData.positions) {
        const data = new Float32Array(vertexData.positions)
        sketchMeshes.forEach((sketchMesh) => {
          sketchMesh.setVerticesData(VertexBuffer.PositionKind, data)
        })
      }

      if (isBimObjectMesh(windowMesh) && windowMesh.metadata.templateMeshId) {
        syncronizeBimObjectMeshWithTemplate(windowMesh)
      }

      const openingMeshes = wallMesh.getChildMeshes(
        true,
        (node): node is Mesh =>
          node instanceof Mesh && (isWindowMesh(node) || isDoorMesh(node)),
      )
      cutOpeningHolesInWallMesh(wallMesh, openingMeshes, true)

      windowProps[WindowProperty.Width].anchor = anchor
    },
    [windowMesh],
  )

  const changeWindowHeight = useCallback(
    (value: number, anchor: WindowAnchor = WindowAnchor.Center) => {
      const wallMesh =
        windowMesh.parent instanceof Mesh && isWallMesh(windowMesh.parent)
          ? (windowMesh.parent as Mesh)
          : undefined
      if (!isWindowMesh(windowMesh) || !wallMesh) {
        console.warn(
          `useEditWindowTool supports only meshes of 'Window' type with parent mesh of 'Wall' type`,
          {
            mesh: windowMesh,
            parent: windowMesh.parent,
          },
        )

        return
      }

      const windowProps = windowPropsMap.get(windowMesh.id)?.props
      if (!windowProps) {
        return
      }

      const wallBoundingBox = wallMesh.getBoundingInfo().boundingBox
      const { y: wallHalfHeight } = wallBoundingBox.extendSize

      let windowMeshWithGeometry
      if (!isBimObjectMesh(windowMesh)) {
        windowMeshWithGeometry = windowMesh
      } else {
        if (windowMesh.metadata.templateMeshId) {
          windowMeshWithGeometry = windowMesh
            .getScene()
            .getMeshById(windowMesh.metadata.templateMeshId) as Mesh
        }
      }
      if (!windowMeshWithGeometry) {
        windowMeshWithGeometry = createBimObjectTemplateMesh(windowMesh)
      }

      const windowBoundingBox =
        windowMeshWithGeometry.getBoundingInfo().boundingBox
      const {
        x: windowHalfWidth,
        y: windowHalfHeight,
        z: windowHalfThickness,
      } = windowBoundingBox.extendSize

      const { sillHeight } = calculateWindowProps(
        windowMeshWithGeometry,
        wallMesh,
      )
      let newHeight = Math.max(value ?? windowHalfHeight * 2, 1e-3)
      // Safe area (1e-3) need to avoid wall collapsing during CSG
      switch (anchor) {
        case WindowAnchor.Start:
          // Max window height should be limited to upper side of the wall
          newHeight = Math.min(
            newHeight,
            wallHalfHeight * 2 - sillHeight - 1e-3,
          )
          break
        case WindowAnchor.Center:
          // Max window height should be limited to the closest side (upper or bottom) of the wall
          newHeight = Math.min(
            newHeight,
            (wallHalfHeight -
              1e-3 -
              Math.abs(windowMeshWithGeometry.position.y)) *
              2,
          )
          break
        case WindowAnchor.End:
          // Max window height should be limited to bottom side of the wall
          newHeight = Math.min(
            newHeight,
            windowHalfHeight * 2 + sillHeight - 1e-3,
          )
          break
      }

      // No need to resize window if current width is equal to passed value
      if (equalsWithEpsilon(newHeight, windowHalfHeight * 2)) {
        return
      }

      const sketchMeshes = windowMeshWithGeometry
        .getChildMeshes(true, (node): node is Mesh => node instanceof Mesh)
        .reduce<Mesh[]>((acc, current) => {
          if (isSketchMesh(current)) {
            acc.push(current)
          }

          return acc
        }, [])

      const vertexData = CreateBoxVertexData({
        width: windowHalfWidth * 2,
        height: newHeight,
        depth: windowHalfThickness * 2,
        faceColors: [
          Color4.FromInts(255, 255, 255, 255),
          Color4.FromInts(255, 255, 255, 255),
          Color4.FromInts(255, 255, 255, 255),
          Color4.FromInts(255, 255, 255, 255),
          Color4.FromInts(0, 0, 0, 255),
          Color4.FromInts(255, 255, 255, 255),
        ],
      })

      vertexData.applyToMesh(windowMeshWithGeometry)

      if (anchor === WindowAnchor.Start || anchor === WindowAnchor.End) {
        const diff = newHeight - windowHalfHeight * 2
        windowMeshWithGeometry.position.y +=
          anchor === WindowAnchor.Start ? diff / 2 : -diff / 2
      }

      // Trigger Measurements UI position change
      windowMeshWithGeometry.computeWorldMatrix(true)

      const selectionManager = SelectionManager.getInstance()
      if (selectionManager.isSelected(windowMesh.id)) {
        // Update selection edges
        windowMesh.enableEdgesRendering(0.9999)
      }

      // Update Sketch mesh of window
      if (vertexData.positions) {
        const data = new Float32Array(vertexData.positions)
        sketchMeshes.forEach((sketchMesh) => {
          sketchMesh.setVerticesData(VertexBuffer.PositionKind, data)
        })
      }

      if (isBimObjectMesh(windowMesh) && windowMesh.metadata.templateMeshId) {
        syncronizeBimObjectMeshWithTemplate(windowMesh)
      }

      const openingMeshes = wallMesh.getChildMeshes(
        true,
        (node): node is Mesh =>
          node instanceof Mesh && (isWindowMesh(node) || isDoorMesh(node)),
      )
      cutOpeningHolesInWallMesh(wallMesh, openingMeshes, true)

      windowProps[WindowProperty.Height].anchor = anchor
    },
    [windowMesh],
  )

  const changeWindowPosition = useCallback(
    (payload: ChangeWindowPositionOptions) => {
      const wallMesh =
        windowMesh.parent instanceof Mesh && isWallMesh(windowMesh.parent)
          ? (windowMesh.parent as Mesh)
          : undefined
      if (!isWindowMesh(windowMesh) || !wallMesh) {
        console.warn(
          `useEditWindowTool supports only meshes of 'Window' type with parent mesh of 'Wall' type`,
          {
            mesh: windowMesh,
            parent: windowMesh.parent,
          },
        )

        return
      }

      let windowMeshWithGeometry
      if (!isBimObjectMesh(windowMesh)) {
        windowMeshWithGeometry = windowMesh
      } else {
        if (windowMesh.metadata.templateMeshId) {
          windowMeshWithGeometry = windowMesh
            .getScene()
            .getMeshById(windowMesh.metadata.templateMeshId) as Mesh
        }
      }
      if (!windowMeshWithGeometry) {
        console.error('Can not find window template')
        return
      }

      const wallBoundingBox = wallMesh.getBoundingInfo().boundingBox
      const { x: wallHalfLength, y: wallHalfHeight } =
        wallBoundingBox.extendSize

      const { height, centerLocationOnWall, sillHeight } = calculateWindowProps(
        windowMeshWithGeometry,
        wallMesh,
      )
      const newCenterLocationOnWall =
        payload.centerLocationOnWall ?? centerLocationOnWall
      // Limit sill height to be within wall boundaries
      const newSillHeight = Math.max(
        Math.min(
          payload.sillHeight ?? sillHeight,
          wallHalfHeight * 2 - height - 1e-3,
        ),
        1e-3,
      )

      // No need to move window if current center location on wall and sill height is equal to passed values
      if (
        equalsWithEpsilon(newCenterLocationOnWall, centerLocationOnWall) &&
        equalsWithEpsilon(newSillHeight, sillHeight)
      ) {
        return
      }

      windowMeshWithGeometry.position.x =
        -wallHalfLength + newCenterLocationOnWall
      windowMeshWithGeometry.position.y =
        -wallHalfHeight + height / 2 + newSillHeight

      // Trigger Measurements UI position change
      windowMeshWithGeometry.computeWorldMatrix(true)
      if (isBimObjectMesh(windowMesh) && windowMesh.metadata.templateMeshId) {
        syncronizeBimObjectMeshWithTemplate(windowMesh)
      }

      const openingMeshes = wallMesh.getChildMeshes(
        true,
        (node): node is Mesh =>
          node instanceof Mesh && (isWindowMesh(node) || isDoorMesh(node)),
      )
      cutOpeningHolesInWallMesh(wallMesh, openingMeshes, true)
    },
    [windowMesh],
  )

  const saveChanges = useCallback(
    (option?: SaveResetOptions) => {
      const wallMesh =
        windowMesh.parent instanceof Mesh && isWallMesh(windowMesh.parent)
          ? (windowMesh.parent as Mesh)
          : undefined
      if (!isWindowMesh(windowMesh) || !wallMesh) {
        console.warn(
          `useEditWindowTool supports only meshes of 'Window' type with parent mesh of 'Wall' type`,
          {
            mesh: windowMesh,
            parent: windowMesh.parent,
          },
        )

        return
      }

      const windowProps = windowPropsMap.get(windowMesh.id)?.props
      if (!windowProps) {
        return
      }

      const savedData = {
        width: windowProps[WindowProperty.Width].value,
        height: windowProps[WindowProperty.Height].value,
        centerLocationOnWall: windowProps[WindowProperty.CenterLocationOnWall],
        sillHeight: windowProps[WindowProperty.SillHeight],
      }
      const options = option ? (Array.isArray(option) ? option : [option]) : []

      const { width, height, centerLocationOnWall, sillHeight } =
        calculateWindowProps(windowMesh, wallMesh)
      if (!options.length || options.includes(WindowProperty.Width)) {
        savedData.width = width
      }

      if (!options.length || options.includes(WindowProperty.Height)) {
        savedData.height = height
        savedData.sillHeight = sillHeight
      }

      if (
        !options.length ||
        options.includes(WindowProperty.CenterLocationOnWall)
      ) {
        savedData.centerLocationOnWall = centerLocationOnWall
      }

      if (!options.length || options.includes(WindowProperty.SillHeight)) {
        savedData.sillHeight = sillHeight
      }

      if (
        !equalsWithEpsilon(
          windowProps[WindowProperty.Width].value,
          savedData.width,
        ) ||
        !equalsWithEpsilon(
          windowProps[WindowProperty.Height].value,
          savedData.height,
        ) ||
        !equalsWithEpsilon(
          windowProps[WindowProperty.CenterLocationOnWall],
          savedData.centerLocationOnWall,
        ) ||
        !equalsWithEpsilon(
          windowProps[WindowProperty.SillHeight],
          savedData.sillHeight,
        )
      ) {
        const { width, height, centerLocationOnWall, sillHeight } = savedData
        dispatch(
          editWindow({
            width,
            height,
            id: windowMesh.id,
            wall_id: wallMesh.id,
            sill_height: sillHeight,
            center_location_on_wall: centerLocationOnWall,
          }),
        )

        const convertData = store.getState().construction.convertData
        dispatch(
          updateHistory({
            convertData,
            items: [
              {
                itemId: windowMesh.id,
                actionType: ActionType.EDIT,
              },
            ],
          }),
        )

        windowProps[WindowProperty.Width].value = width
        windowProps[WindowProperty.Height].value = height
        windowProps[WindowProperty.CenterLocationOnWall] = centerLocationOnWall
        windowProps[WindowProperty.SillHeight] = sillHeight

        createWallGaps()
      }
    },
    [windowMesh, dispatch],
  )

  const resetChanges = useCallback(
    (option?: SaveResetOptions) => {
      if (!isWindowMesh(windowMesh)) {
        console.warn(
          `useEditWindowTool supports only meshes of 'Window' type with parent mesh of 'Wall' type`,
          {
            mesh: windowMesh,
            parent: windowMesh.parent,
          },
        )

        return
      }

      const windowProps = windowPropsMap.get(windowMesh.id)?.props
      if (!windowProps) {
        return
      }

      const options = option ? (Array.isArray(option) ? option : [option]) : []

      if (!options.length || options.includes(WindowProperty.Width)) {
        const { value, anchor } = windowProps[WindowProperty.Width]
        changeWindowWidth(value, anchor)
      }

      if (!options.length || options.includes(WindowProperty.Height)) {
        const { value, anchor } = windowProps[WindowProperty.Height]
        changeWindowHeight(value, anchor)
      }

      if (
        !options.length ||
        options.includes(WindowProperty.CenterLocationOnWall) ||
        options.includes(WindowProperty.SillHeight)
      ) {
        const shouldResetCenterLocationOnWall =
          !options.length ||
          options.includes(WindowProperty.CenterLocationOnWall)
        const shouldResetSillHeight =
          !options.length || options.includes(WindowProperty.SillHeight)

        changeWindowPosition({
          centerLocationOnWall: shouldResetCenterLocationOnWall
            ? windowProps[WindowProperty.CenterLocationOnWall]
            : undefined,
          sillHeight: shouldResetSillHeight
            ? windowProps[WindowProperty.SillHeight]
            : undefined,
        })
      }
    },
    [windowMesh, changeWindowWidth, changeWindowHeight, changeWindowPosition],
  )

  useEffect(() => {
    const wallMesh =
      windowMesh.parent instanceof Mesh && isWallMesh(windowMesh.parent)
        ? (windowMesh.parent as Mesh)
        : undefined
    if (!isWindowMesh(windowMesh) || !wallMesh) {
      console.warn(
        `useEditWindowTool supports only meshes of 'Window' type with parent mesh of 'Wall' type`,
        {
          mesh: windowMesh,
          parent: windowMesh.parent,
        },
      )

      return
    }

    if (!windowPropsMap.has(windowMesh.id)) {
      const { width, height, centerLocationOnWall, sillHeight } =
        calculateWindowProps(windowMesh, wallMesh)

      windowPropsMap.set(windowMesh.id, {
        count: 1,
        props: {
          [WindowProperty.Width]: {
            value: width,
            anchor: WindowAnchor.Center,
          },
          [WindowProperty.Height]: {
            value: height,
            anchor: WindowAnchor.Center,
          },
          [WindowProperty.CenterLocationOnWall]: centerLocationOnWall,
          [WindowProperty.SillHeight]: sillHeight,
        },
      })
    } else {
      const obj = windowPropsMap.get(windowMesh.id)
      if (obj) {
        obj.count += 1
      }
    }

    let templateMesh: Nullable<Mesh> = null
    let action: Nullable<ExecuteCodeAction> = null
    if (addAction) {
      const localMatrix = new Matrix()
      const tmpVec = new Vector3()
      action = new ExecuteCodeAction(
        ActionManager.OnPickDownTrigger,
        ({ sourceEvent }) => {
          const isCtrlPressed = sourceEvent.ctrlKey
          const scene = SceneManager.getInstance().getScene()
          const isSeparateWallUIVisible = SeparateElementUI.isUIVisible
          if (isCtrlPressed || !scene || isSeparateWallUIVisible) {
            return
          }

          const transform = templateMesh
            ? templateMesh.getWorldMatrix()
            : windowMesh.getWorldMatrix()
          const windowDirection = Vector3.TransformNormalFromFloatsToRef(
            1,
            0,
            0,
            transform,
            new Vector3(),
          )
          const windowUp = Vector3.TransformNormalFromFloatsToRef(
            0,
            1,
            0,
            transform,
            new Vector3(),
          )
          const windowNormal = Vector3.TransformNormalFromFloatsToRef(
            0,
            0,
            1,
            transform,
            new Vector3(),
          )

          const ray = scene.createPickingRay(
            scene.pointerX,
            scene.pointerY,
            null,
            scene.activeCamera,
          )
          const windowBoundingBox = templateMesh
            ? templateMesh.getBoundingInfo().boundingBox
            : windowMesh.getBoundingInfo().boundingBox
          const { pickNormal, pickPoint } =
            rayBoundingBoxIntersection(ray, windowBoundingBox) ?? {}
          if (!pickNormal || !pickPoint) {
            return
          }

          transform.invertToRef(localMatrix)

          let anchor: Nullable<WindowAnchor> = null

          // Window Width Change
          if (isOrthoLinear(pickNormal, windowDirection)) {
            const isFromBeginning = isAntiParallel(pickNormal, windowDirection)
            anchor = isFromBeginning ? WindowAnchor.End : WindowAnchor.Start
          }
          // Window Height Change
          else if (isOrthoLinear(pickNormal, windowUp)) {
            const isFromBeginning = isAntiParallel(pickNormal, windowUp)
            anchor = isFromBeginning ? WindowAnchor.End : WindowAnchor.Start
          }
          // Window Position Change or Resize
          else if (isOrthoLinear(pickNormal, windowNormal)) {
            const edgePoint = Math.min(
              windowBoundingBox.extendSize.x * 0.25,
              windowBoundingBox.extendSize.y * 0.25,
            )

            Vector3.TransformCoordinatesToRef(pickPoint, localMatrix, tmpVec)

            const isInTheMiddleX =
              Math.abs(tmpVec.x) <= windowBoundingBox.extendSize.x - edgePoint
            const isInTheMiddleY =
              Math.abs(tmpVec.y) <= windowBoundingBox.extendSize.y - edgePoint
            if (!isInTheMiddleX || !isInTheMiddleY) {
              if (Math.abs(tmpVec.x) > Math.abs(tmpVec.y)) {
                pickNormal.set(tmpVec.x > 0 ? 1 : -1, 0, 0)
                Vector3.TransformNormalToRef(pickNormal, transform, pickNormal)

                const isFromBeginning = isAntiParallel(
                  pickNormal,
                  windowDirection,
                )
                anchor = isFromBeginning ? WindowAnchor.End : WindowAnchor.Start
              } else {
                pickNormal.set(0, tmpVec.y > 0 ? 1 : -1, 0)
                Vector3.TransformNormalToRef(pickNormal, transform, pickNormal)

                const isFromBeginning = isAntiParallel(pickNormal, windowUp)
                anchor = isFromBeginning ? WindowAnchor.End : WindowAnchor.Start
              }
            }
          } else {
            return
          }

          const pointerDragBehavior = new PointerDragBehavior(
            anchor
              ? { dragAxis: pickNormal }
              : { dragPlaneNormal: windowNormal },
          )
          pointerDragBehavior.useObjectOrientationForDragging = false
          pointerDragBehavior.moveAttached = false

          let totalDragDistanceX = 0
          let totalDragDistanceY = 0
          const gridSettings = store.getState().construction.gridSettings
          const cellSize = gridSettings.snapToGrid ? gridSettings.cellSize : 1
          const { width, height, centerLocationOnWall, sillHeight } =
            calculateWindowProps(
              templateMesh ? templateMesh : windowMesh,
              wallMesh,
            )
          pointerDragBehavior.onDragObservable.add(({ delta }) => {
            Vector3.TransformNormalToRef(delta, localMatrix, tmpVec)

            if (anchor) {
              if (!isBimObjectMesh(windowMesh) || !bimObject?.isNonEditable) {
                if (Math.abs(tmpVec.x) > 0) {
                  totalDragDistanceX += tmpVec.x

                  let newWidth =
                    anchor === WindowAnchor.Start
                      ? width + totalDragDistanceX
                      : width - totalDragDistanceX

                  if (gridSettings.snapToGrid) {
                    newWidth = Math.round(newWidth / cellSize) * cellSize
                  }

                  changeWindowWidth(newWidth, anchor)
                } else if (Math.abs(tmpVec.y) > 0) {
                  totalDragDistanceY += tmpVec.y

                  let newHeight =
                    anchor === WindowAnchor.Start
                      ? height + totalDragDistanceY
                      : height - totalDragDistanceY

                  if (gridSettings.snapToGrid) {
                    newHeight = Math.round(newHeight / cellSize) * cellSize
                  }

                  changeWindowHeight(newHeight, anchor)
                }
              }
            } else {
              totalDragDistanceX += tmpVec.x
              let newCenterLocationOnWall =
                centerLocationOnWall + totalDragDistanceX

              totalDragDistanceY += tmpVec.y
              let newSillHeight = sillHeight + totalDragDistanceY

              if (gridSettings.snapToGrid) {
                newCenterLocationOnWall =
                  Math.round(newCenterLocationOnWall / cellSize) * cellSize
                newSillHeight = Math.round(newSillHeight / cellSize) * cellSize
              }

              changeWindowPosition({
                centerLocationOnWall: newCenterLocationOnWall,
                sillHeight: newSillHeight,
              })
            }
          })

          pointerDragBehavior.onDragEndObservable.add(() => {
            if (isBimObjectMesh(windowMesh) && templateMesh) {
              templateMesh.removeBehavior(pointerDragBehavior)
            } else {
              windowMesh.removeBehavior(pointerDragBehavior)
            }

            saveChanges()
          })

          if (isBimObjectMesh(windowMesh) && templateMesh) {
            templateMesh.addBehavior(pointerDragBehavior)
          } else {
            windowMesh.addBehavior(pointerDragBehavior)
          }
        },
      )

      if (isBimObjectMesh(windowMesh)) {
        templateMesh = createBimObjectTemplateMesh(windowMesh)
        templateMesh.actionManager?.registerAction(action)
      } else {
        windowMesh.actionManager?.registerAction(action)
      }
    }

    return () => {
      if (action) {
        if (isBimObjectMesh(windowMesh) && templateMesh) {
          templateMesh.actionManager?.unregisterAction(action)
          removeBimObjectTemplateMesh(windowMesh)
        } else {
          windowMesh.actionManager?.unregisterAction(action)
        }
      }

      const obj = windowPropsMap.get(windowMesh.id)
      if (obj) {
        obj.count -= 1
        if (!obj.count) {
          const { width, height, centerLocationOnWall, sillHeight } =
            calculateWindowProps(windowMesh, wallMesh)
          if (
            !equalsWithEpsilon(obj.props[WindowProperty.Width].value, width) ||
            !equalsWithEpsilon(
              obj.props[WindowProperty.Height].value,
              height,
            ) ||
            !equalsWithEpsilon(
              obj.props[WindowProperty.CenterLocationOnWall],
              centerLocationOnWall,
            ) ||
            !equalsWithEpsilon(obj.props[WindowProperty.SillHeight], sillHeight)
          ) {
            resetChanges()
          }
          windowPropsMap.delete(windowMesh.id)
        }
      }
    }
  }, [
    windowMesh,
    addAction,
    changeWindowWidth,
    changeWindowHeight,
    changeWindowPosition,
    saveChanges,
    resetChanges,
  ])

  return {
    changeWindowWidth,
    changeWindowHeight,
    changeWindowPosition,
    saveChanges,
    resetChanges,
  }
}
